/**
 * bonusImpactExplainer — translates a raw support-role bonus into a
 * concrete business-KPI explanation for the apprenant.
 *
 * Goal : where the bonus `label` says "+10% revenus contrats classiques",
 * this util returns "+~6 pts de marge brute par contrat sans changer vos
 * coûts opérationnels", anchoring the perk in a measurable P&L impact.
 *
 * Used by :
 *   - SupportHireCelebration popup (recruitment moment)
 *   - _buildWelcomeMessage email body
 *   - FonctionsSupport "Impact métier" panel
 *
 * 03/06/2026 — Created from user feedback : "ex de léa au commerce
 * apporte marge brute etc...".
 */

const KPI_MAP = {
  // Revenue boosts
  revenue_classical:   { kpi: 'Marge brute', detail: 'Le CA augmente sans toucher aux coûts variables (carburant, RH). Concrètement : +6 à +9 pts de marge brute par contrat classique.' },
  revenue_marketing:   { kpi: 'Marge brute', detail: 'Les leads marketing améliorent le taux de transformation. Sur les contrats acquis : +5 à +8 pts de marge.' },
  revenue_premium:     { kpi: 'Marge nette', detail: 'Les contrats premium (>500€/j) tirent la marge nette vers le haut : effet TCO global réduit.' },
  // Cost reductions
  maintenance_discount:{ kpi: 'TCO véhicules', detail: 'Une maintenance préventive optimisée réduit les pannes imprévues. Concrètement : −15 à −25 % sur la ligne entretien mensuelle.' },
  fuel_discount:       { kpi: 'Coût d\'exploitation', detail: 'Un meilleur routing + écoconduite : −5 à −12 % carburant sur la flotte active.' },
  salary_discount:     { kpi: 'Masse salariale', detail: 'L\'optimisation des plannings et la gestion des heures sup baisse le coût employeur : −3 à −8 %.' },
  // Operational
  notoriety_passive:   { kpi: 'Notoriété', detail: 'Communication continue → meilleure visibilité dans les appels d\'offres et bouche-à-oreille : +1 à +2 pts/mois.' },
  reputation_passive:  { kpi: 'Réputation client', detail: 'Suivi proactif des clients : score CRM moyen +5 à +10 pts sur 6 mois.' },
  satisfaction_passive:{ kpi: 'Climat social', detail: 'Soutien RH régulier : satisfaction des chauffeurs +3 à +6 pts, réduit l\'absentéisme et le turnover.' },
  fatigue_discount:    { kpi: 'Fatigue chauffeurs', detail: 'Planification respectueuse des temps de repos : fatigue accumulée −15 % par jour ouvré.' },
  recruitment_speed:   { kpi: 'Cycle de recrutement', detail: 'Sourcing accéléré : nouveaux chauffeurs disponibles 30 à 50 % plus vite.' },
  loyalty_boost:       { kpi: 'Fidélisation client', detail: 'Relances et suivi qualité : score CRM des clients existants +1 pt/semaine de service.' },
  payment_speed:       { kpi: 'BFR', detail: 'Suivi des factures + relance auto : DSO réduit de 8 à 15 jours, BFR allégé.' },
  invoice_speed:       { kpi: 'Trésorerie disponible', detail: 'Facturation immédiate à la livraison : encaissements +5 à +10 jours plus tôt.' },
  // Special unlocks (e.g. Sylvie Marchand → access to T4-T5 tier contracts)
  tier_access:         { kpi: 'Mix portefeuille', detail: 'Accès débloqué à des contrats premium (Tier 4-5) : levier de croissance vers le haut de gamme.' },
  contract_count:      { kpi: 'Pipeline commercial', detail: 'Plus de contrats simultanés sur le marché : choix élargi, meilleure couverture flotte.' },
};

/**
 * Heuristic bonus → impact lookup. Falls back to a generic explainer
 * when the bonus type is not in the map.
 *
 * 04/06/2026 — Now takes an optional `context` describing the current
 * game state. When a "passive" bonus targets a stat that's already near
 * its ceiling (reputation ≥ 90, notoriety ≥ 85, satisfaction ≥ 85),
 * the explainer adds a `saturated: true` flag and tweaks the detail
 * sentence so the apprentice knows the bonus will have **limited
 * marginal impact**.
 *
 * @param {{ type:string, value:number, label?:string }} bonus
 * @param {{ reputation?:number, notoriety?:number, avgSatisfaction?:number }} [context]
 * @returns {{ kpi:string, detail:string, saturated?:boolean }}
 */
export function getBonusImpactExplainer(bonus, context = {}) {
  if (!bonus) return { kpi: 'Performance', detail: 'Effet positif sur la performance globale.' };
  const t = bonus.type || '';
  let result = null;
  // Direct map hits.
  if (KPI_MAP[t]) result = { ...KPI_MAP[t] };

  // Fuzzy keyword-based fallback (covers legacy / variant types).
  if (!result) {
    const txt = `${t} ${(bonus.label || '')}`.toLowerCase();
    if (txt.includes('revenu') || txt.includes('revenue') || txt.includes('chiffre')) result = { ...KPI_MAP.revenue_classical };
    else if (txt.includes('maintenance') || txt.includes('entretien')) result = { ...KPI_MAP.maintenance_discount };
    else if (txt.includes('fuel') || txt.includes('carburant')) result = { ...KPI_MAP.fuel_discount };
    else if (txt.includes('salar')) result = { ...KPI_MAP.salary_discount };
    else if (txt.includes('noto')) result = { ...KPI_MAP.notoriety_passive };
    else if (txt.includes('reput') || txt.includes('réput')) result = { ...KPI_MAP.reputation_passive };
    else if (txt.includes('satisf') || txt.includes('climat')) result = { ...KPI_MAP.satisfaction_passive };
    else if (txt.includes('fatig')) result = { ...KPI_MAP.fatigue_discount };
    else if (txt.includes('recru')) result = { ...KPI_MAP.recruitment_speed };
    else if (txt.includes('loyal') || txt.includes('fidél')) result = { ...KPI_MAP.loyalty_boost };
    else if (txt.includes('paiement') || txt.includes('payment')) result = { ...KPI_MAP.payment_speed };
    else if (txt.includes('factur') || txt.includes('invoice')) result = { ...KPI_MAP.invoice_speed };
    else if (txt.includes('tier') || txt.includes('premium')) result = { ...KPI_MAP.tier_access };
    else if (txt.includes('contrat') && (txt.includes('+') || txt.includes('pipeline'))) result = { ...KPI_MAP.contract_count };
    else result = { kpi: 'Performance', detail: 'Effet positif sur la performance globale de l\'entreprise.' };
  }

  // Saturation check — flag the bonus when its target stat is near the cap.
  // Helps the apprentice realise that hiring more "reputation_passive"
  // roles will not move the needle past the soft cap.
  const isRep = /reput|réput/.test(`${t} ${bonus.label || ''}`.toLowerCase());
  const isNoto = /noto/.test(`${t} ${bonus.label || ''}`.toLowerCase());
  const isSat = /satisf|climat/.test(`${t} ${bonus.label || ''}`.toLowerCase());
  const repNear = Number.isFinite(context.reputation) && context.reputation >= 90;
  const notoNear = Number.isFinite(context.notoriety) && context.notoriety >= 85;
  const satNear = Number.isFinite(context.avgSatisfaction) && context.avgSatisfaction >= 85;

  if ((isRep && repNear) || (isNoto && notoNear) || (isSat && satNear)) {
    result.saturated = true;
    result.detail =
      `⚠ Impact marginal limité — votre indicateur actuel est déjà très élevé. ${result.detail}`;
  }

  return result;
}

export { KPI_MAP };
